import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import LegalFooter from '@/components/LegalFooter';
import Seo from '@/components/Seo';
import { MarkdownArticle } from '@/components/blog/MarkdownArticle';
import { formatPostDate } from './Blog';
import { rememberGuideTouch } from '@/lib/guideAttribution';

/**
 * Single guide (/blog/:slug) — Phase 131. Loads the post from
 * `GET /api/public/blog/:slug`, renders markdown, emits Article JSON-LD.
 * Unknown/unpublished slugs redirect to the hub.
 */
export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        api.get(`/public/blog/${encodeURIComponent(slug)}`)
            .then((r) => { setPost(r.data); rememberGuideTouch(r.data?.slug || slug); })
            .catch(() => setNotFound(true));
        api.get('/public/blog')
            .then((r) => setRelated((r.data?.items || []).filter((a) => a.slug !== slug).slice(0, 3)))
            .catch(() => setRelated([]));
        if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
    }, [slug]);

    if (notFound) return <Navigate to="/blog" replace />;

    const articleJsonLd = post && {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.seo_description || post.excerpt,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: { '@type': 'Organization', name: post.author?.name || 'SplashPad', url: 'https://splashpad.pro' },
        publisher: {
            '@type': 'Organization',
            name: 'SplashPad',
            logo: { '@type': 'ImageObject', url: 'https://splashpad.pro/splashpad-logo.png' },
        },
        mainEntityOfPage: `https://splashpad.pro/blog/${post.slug}`,
        image: post.cover_image_url || 'https://splashpad.pro/splashpad-logo.png',
        keywords: (post.tags || []).join(', '),
    };

    return (
        <div className="min-h-screen bg-[#f6fbff] text-[#0a1628]" data-testid="blog-post-page">
            {post && (
                <Seo
                    title={`${post.seo_title || post.title} | SplashPad Guides`}
                    description={post.seo_description || post.excerpt}
                    canonical={`https://splashpad.pro/blog/${post.slug}`}
                    type="article"
                    publishedTime={post.published_at}
                    modifiedTime={post.updated_at}
                    image={post.cover_image_url || undefined}
                    keywords={(post.tags || []).join(', ')}
                    jsonLd={articleJsonLd}
                />
            )}
            <header className="border-b border-[#0a1628]/10 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="font-display text-lg font-bold text-[#0a1628]" data-testid="blog-post-logo-link">SplashPad</Link>
                    <div className="flex items-center gap-3">
                        <Link to="/blog" className="text-sm font-semibold text-[#1a3a5c] hover:text-[#0a1628]" data-testid="blog-post-back-to-hub">Guides</Link>
                        <Button asChild className="h-10 rounded-xl bg-[#0a1628] px-4 text-sm font-semibold text-white hover:bg-[#0a1628]/90" data-testid="blog-post-cta-signup">
                            <Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </header>

            <article className="bg-white">
                <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
                    <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1a3a5c] hover:text-[#0a1628]" data-testid="blog-post-back-link">
                        <ArrowLeft className="h-3.5 w-3.5" /> All guides
                    </Link>

                    {!post ? (
                        <div className="mt-8" data-testid="blog-post-loading">
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="mt-4 h-10 w-full" />
                            <Skeleton className="mt-2 h-10 w-2/3" />
                            <Skeleton className="mt-6 h-4 w-40" />
                            <Skeleton className="mt-8 h-24 w-full" />
                            <Skeleton className="mt-4 h-40 w-full" />
                        </div>
                    ) : (
                        <>
                            <Badge className="mb-4 mt-6 border-[#ffd70066] bg-[#ffd70022] text-[#1a3a5c] hover:bg-[#ffd70033]" data-testid="blog-post-badge">
                                <BookOpen className="mr-1 h-3 w-3" /> Guide
                            </Badge>
                            <h1 className="font-display text-3xl font-bold leading-tight text-[#0a1628] sm:text-4xl lg:text-5xl" data-testid="blog-post-title">
                                {post.title}
                            </h1>
                            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/60" data-testid="blog-post-meta">
                                {formatPostDate(post.published_at)} · {post.read_time_min} min read · {post.author?.name || 'SplashPad Team'}
                            </div>
                            <p className="mt-6 text-lg leading-relaxed text-[#1a3a5c]" data-testid="blog-post-excerpt">{post.excerpt}</p>
                            {post.cover_image_url && (
                                <img src={post.cover_image_url} alt="" className="mt-8 w-full rounded-2xl" data-testid="blog-post-cover" />
                            )}
                            <MarkdownArticle content={post.content_md} className="mt-6" />
                        </>
                    )}

                    <div className="mt-14 rounded-2xl border border-[#0a1628]/10 bg-[#f6fbff] p-6 sm:p-8">
                        <h3 className="font-display text-xl font-bold text-[#0a1628]">Run your swim school like a pro</h3>
                        <p className="mt-2 text-sm text-[#1a3a5c]/80">
                            Registration, waivers, payments, staff and payroll, and per-location reporting — one login for you,
                            your coaches, and every family. Free 30-day trial. Just $0.25 per transaction.
                        </p>
                        <div className="mt-5">
                            <Button asChild className="h-11 rounded-xl bg-[#0a1628] px-5 text-sm font-semibold text-white hover:bg-[#0a1628]/90" data-testid="blog-post-inline-cta">
                                <Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </article>

            {related.length > 0 && (
                <section className="bg-[#f6fbff]">
                    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                        <h2 className="font-display text-2xl font-bold text-[#0a1628]" data-testid="blog-post-related-heading">More guides</h2>
                        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-post-related-grid">
                            {related.map((a) => (
                                <Link key={a.slug} to={`/blog/${a.slug}`} className="group rounded-2xl border border-[#0a1628]/10 bg-white p-6 shadow-sm transition-[border-color,box-shadow] hover:border-[#00d4ff] hover:shadow-md" data-testid={`blog-post-related-${a.slug}`}>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/60">{a.read_time_min} min read</div>
                                    <h3 className="mt-2 font-display text-lg font-bold leading-snug text-[#0a1628] group-hover:text-[#1a3a5c]">{a.title}</h3>
                                    <p className="mt-2 line-clamp-3 text-sm text-[#1a3a5c]/80">{a.excerpt}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
            <LegalFooter />
        </div>
    );
}
